// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';

// Third party
import { takeUntil } from 'rxjs';

// Local library
import {
  AccountFacade,
  WageringPreferencesService
} from '@amwager/account/data-access';
import { TranslatePipe, UnsubscribeObservableService } from '@amwager/util';

/**
 * Wagering preferences form (sort order, wager toggles, event-type filter)
 */
@Component({
  selector: 'feature-mat-wagering-preferences',
  templateUrl: './wagering-preferences.component.html',
  styleUrls: ['./wagering-preferences.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslatePipe
  ]
})
export class WageringPreferencesComponent
  extends UnsubscribeObservableService
  implements OnInit {

  sortBy = '';
  isClearWager = false;
  requireConfirmation = false;
  isExoticSelections = false;
  eventTypeFilter: string[] = [];

  // Guards double-taps while a save round-trips
  isDisabled = false;

  constructor(
    private readonly accountFacade: AccountFacade,
    private readonly wageringPreferencesService: WageringPreferencesService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.accountFacade.getAccountPreferencesAPI();

    this.accountFacade.isGetAccountPreferencesSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((callback) => {
        if (callback?.isSuccess === undefined) return;

        const response = callback?.response?.AccountPreferences;
        if (!response) return;

        this.sortBy = response.SortBy;
        this.isClearWager = response.IsClearWager;
        this.requireConfirmation = response.RequireConfirmation;
        this.isExoticSelections = response.IsExoticSelections;
        this.eventTypeFilter = response.EventTypeFilter ?? [];

        this.isDisabled = false;
        this.cdRef.markForCheck();
      });
  }

  /**
   * Apply a preference toggle and persist it via the account facade
   * @param {string} type UI key of the toggled preference
   * @returns {void}
   */
  setPreference(type: string): void {
    if (this.isDisabled) return;

    const update = this.wageringPreferencesService.resolveUpdate(type, {
      isClearWager: this.isClearWager,
      requireConfirmation: this.requireConfirmation,
      isExoticSelections: this.isExoticSelections,
      eventTypeFilter: this.eventTypeFilter
    });

    if (!update) return;

    if (update.eventTypeFilter) this.eventTypeFilter = update.eventTypeFilter;
    if (update.fieldName === 'SortBy') this.sortBy = update.value;

    this.isDisabled = true;
    this.accountFacade.upsertAccountPrefenceFieldAPI(update.fieldName, update.value);
  }

  /**
   * True when the given event type is active in the filter
   * @param {string} eventType Capitalized event type (e.g. 'Harness')
   * @returns {boolean} True when present in the filter
   */
  isEventTypeExists(eventType: string): boolean {
    return this.eventTypeFilter?.includes(eventType) ?? false;
  }
}
